// Layer 4 (UI) — the Superintendent's "MTD cumulative tonnes — actual vs
// budget" (locked comp `landing-superintendent.html`, view 4): the actual as a
// solid ink line with its daily points, the budget as a dashed line, the gap
// between them washed state-online while the plant runs ahead, and both lines
// labelled at their end. Hand-drawn SVG, as Planning's year chart: `TrendChart`
// has no dashed comparator line, gap wash or end labels.
// Renders `cumulativeChart()` from SuptLanding.transform.ts; prints no figure of its own.

import { useLayoutEffect, useRef, useState } from 'react';
import type { CumulativeChartModel } from './SuptLanding.transform';

/** Drawn at the card's measured width so type stays at its set size. */
const FALLBACK_W = 640;
const PAD = { l: 50, r: 96, t: 14, b: 26 };

function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_W);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => setWidth(Math.max(320, Math.round(node.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

export function SuptCumulativeChart({ model, height = 250 }: { model: CumulativeChartModel; height?: number }) {
  const [ref, W] = useMeasuredWidth();
  const n = model.actual.length;
  const x = (i: number) => PAD.l + ((W - PAD.l - PAD.r) * i) / Math.max(1, n - 1);
  const y = (v: number) => PAD.t + (height - PAD.t - PAD.b) * (1 - v / model.yMax);
  const line = (points: readonly number[]) =>
    points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join('');
  const last = n - 1;
  // `ahead` is the model's own end-of-window comparison (SuptLanding.transform.ts).
  const gap =
    `${line(model.actual)}` +
    [...model.budget]
      .map((v, i) => ({ v, i }))
      .reverse()
      .map(({ v, i }) => `L${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
      .join('') +
    'Z';
  // End labels keep 12px apart so two close lines never overprint.
  const actualY = y(model.actual[last] ?? 0);
  const budgetY = y(model.budget[last] ?? 0);
  const apart = Math.abs(actualY - budgetY) < 12;
  const actualLabelY = apart && actualY <= budgetY ? budgetY - 12 : actualY;
  return (
    <div ref={ref} className="min-w-0">
      <svg
        viewBox={`0 0 ${W} ${height}`}
        width="100%"
        role="img"
        aria-label={`MTD cumulative tonnes, ${model.actualLabel} dst against ${model.budgetLabel} dst`}
        data-supt-cumulative-chart
        className="block overflow-visible"
      >
        {model.yTicks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(tick)} y2={y(tick)} stroke="var(--hairline-strong)" />
            <text x={PAD.l - 8} y={y(tick) + 3.5} textAnchor="end" fontSize="10.5" fill="var(--ink-tertiary)" className="tabular-nums">
              {tick.toLocaleString('en-US')}
            </text>
          </g>
        ))}
        <path
          d={gap}
          fill={`color-mix(in srgb, var(${model.ahead ? '--state-online' : '--state-warning'}) 11%, transparent)`}
          data-supt-gap={model.ahead ? 'ahead' : 'behind'}
        />
        <path d={line(model.budget)} fill="none" stroke="var(--ink-tertiary)" strokeWidth="1.4" strokeDasharray="5 4" />
        <path d={line(model.actual)} fill="none" stroke="var(--ink-primary)" strokeWidth="1.8" strokeLinejoin="round" />
        {model.actual.map((v, i) => (
          <circle key={i} cx={x(i)} cy={y(v)} r="2.6" fill="var(--surface-2)" stroke="var(--ink-primary)" strokeWidth="1.4" />
        ))}
        <text x={x(last) + 8} y={actualLabelY + 4} fontSize="11.5" fontWeight="600" fill="var(--ink-primary)" className="tabular-nums">
          {model.actualLabel}
        </text>
        <text x={x(last) + 8} y={budgetY + 4} fontSize="11.5" fill="var(--ink-tertiary)" className="tabular-nums">
          {model.budgetLabel}
        </text>
        {model.days.map((day, i) => (
          <text key={day} x={x(i)} y={height - 6} textAnchor="middle" fontSize="10.5" fill="var(--ink-tertiary)" className="tabular-nums">
            {day}
          </text>
        ))}
      </svg>
    </div>
  );
}
